import { add, format, isAfter, isValid, max } from 'date-fns';
import _ from 'platform/utilities/data';
import { SAVED_SEPARATION_DATE } from '../../shared/constants/entryConstants';
import { getToday, isValidFullDate, parseDate } from '../../shared/utils/dates';
import { isCurrentlyActivated, isServingComponent } from './pageDependencies';

// Ignores partial input (e.g. XXXX-02-01 or 0202-02-01) while typing.
const toValidDate = value => {
  const date = isValidFullDate(value) ? parseDate(value) : null;
  return date && isValid(date) ? date : null;
};

// BDD window: more than 89 and at most 180 days from today.
const isInBddWindow = date => {
  const today = getToday();
  return (
    isAfter(date, add(today, { days: 89 })) &&
    !isAfter(date, add(today, { days: 180 }))
  );
};

const getScreenerSeparationDate = () => {
  try {
    return toValidDate(window.sessionStorage.getItem(SAVED_SEPARATION_DATE));
  } catch (error) {
    return null; // storage access blocked
  }
};

/**
 * True when the screener RAD date (session storage) is in the BDD window.
 * @returns {boolean}
 */
export const isScreenerBdd = () => {
  const date = getScreenerSeparationDate();
  return Boolean(date && isInBddWindow(date));
};

// Session storage is wiped on browser close, so `view:isBddData` (persisted
// from the screener) is only used when no date is available to calculate from.
const isBddFromScreener = formData => {
  const screenerDate = getScreenerSeparationDate();
  return screenerDate
    ? isInBddWindow(screenerDate)
    : Boolean(formData?.['view:isBddData']);
};

/**
 * All-claims-v2 BDD calculation. Uses the most specific date entered so far:
 * federal activation anticipated separation date, then the active service
 * exit date, then the screener RAD date.
 * @param {object} formData
 * @returns {boolean}
 */
export const isBDD = formData => {
  const {
    serviceComponent,
    federalActivation,
    exitDate: serviceExitDate,
  } = formData?.serviceInformation || {};
  const isActive = serviceComponent?.active === true;
  const isServing = isServingComponent(formData);
  const answeredNotActivated =
    isServing && federalActivation?.currentlyActivated === false;

  if (isCurrentlyActivated(formData)) {
    const separationDate = toValidDate(
      federalActivation.anticipatedSeparationDate,
    );
    if (separationDate) {
      const exitDate = isActive ? toValidDate(serviceExitDate) : null;
      // Both dates entered: qualifying on either one is enough
      return (
        isInBddWindow(separationDate) ||
        Boolean(exitDate && isInBddWindow(exitDate))
      );
    }
    return isBddFromScreener(formData);
  }

  // Reserves/Guard only, and not currently activated
  if (answeredNotActivated && !isActive) {
    return false;
  }

  const exitDate = isActive ? toValidDate(serviceExitDate) : null;
  if (exitDate) {
    if (isInBddWindow(exitDate)) {
      return true;
    }
    // The screener result holds until a later page can overrule it
    const isPendingActivationAnswer = isServing && !answeredNotActivated;
    return isPendingActivationAnswer ? isBddFromScreener(formData) : false;
  }

  return isBddFromScreener(formData);
};

/**
 * The release date to send as `bddAnticipatedDateOfRelease`: the latest of the
 * federal activation separation date (when currently activated) and the active
 * service exit date that falls in the BDD window, as YYYY-MM-DD. Undefined when
 * neither date qualifies (including when only the screener RAD date qualifies).
 * @param {object} formData
 * @returns {string|undefined}
 */
export const getBddReleaseDate = formData => {
  const { serviceComponent, federalActivation, exitDate } =
    formData?.serviceInformation || {};
  const dates = [];
  if (isCurrentlyActivated(formData)) {
    dates.push(toValidDate(federalActivation.anticipatedSeparationDate));
  }
  if (serviceComponent?.active === true) {
    dates.push(toValidDate(exitDate));
  }
  const inWindow = dates.filter(date => date && isInBddWindow(date));
  return inWindow.length ? format(max(inWindow), 'yyyy-MM-dd') : undefined;
};

export const getBddShaUploads = formData =>
  _.get('separationHealthAssessmentUploads', formData, []);

export const hasShaDocumentUploaded = formData =>
  getBddShaUploads(formData).length > 0;
